import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import OrdersManager from '../services/api/OrdersManager';
import useFeedbackHandler from '../hooks/useFeedbackHandler';
import useDeepRef from '../hooks/utility/useDeepRef';
import { collect } from 'collect.js';
import { OrderFulfillmentStatus } from '../services/exports/Constants';
import OrderFulfillmentsManager from '../services/api/OrderFulfillmentsManager';
import CompleteOrderModal from '../modals/order/CompleteOrderModal';
import OrderDetailsModal from '../modals/order/order-details/OrderDetailsModal';
import RejectOrderModal from '../modals/order/RejectOrderModal';
import { useTranslation } from 'react-i18next';
import OrderHelpers from '../services/helpers/OrderHelpers';
import { DateTime } from 'luxon';

export const OngoingOrdersContext = createContext(null);

export function useOngoingOrders() {
  return useContext(OngoingOrdersContext);
}

export default function OngoingOrdersProvider({ children }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Providers:OngoingOrdersProvider' });

  const { setFeedback } = useFeedbackHandler({ renderToasts: true });

  const { company } = useSelector((state) => state.company);
  const [ordersList, setOrdersList] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  const [showOrderDetailsModal, setShowOrderDetailsModal] = useState(false);
  const [showCompleteOrderModal, setShowCompleteOrderModal] = useState(false);
  const [showRejectOrderModal, setShowRejectOrderModal] = useState(false);

  const ordersListRef = useDeepRef(ordersList);
  const selectedOrderRef = useDeepRef(selectedOrder);

  const activeOrdersList = useMemo(() => collect(ordersList).filter(OrderHelpers.isActive).toArray(), [ordersList]);

  const futureOrdersList = useMemo(
    () =>
      collect(activeOrdersList)
        .filter(OrderHelpers.isFutureOrder)
        .sortBy((item) => DateTime.fromISO(item.estimated_pickup_time).toUnixInteger())
        .toArray(),
    [activeOrdersList],
  );

  const scheduledOrdersList = useMemo(
    () =>
      collect(futureOrdersList)
        .filter(OrderHelpers.isScheduled)
        .sortBy((item) => DateTime.fromISO(item.estimated_pickup_time).toUnixInteger())
        .toArray(),
    [futureOrdersList],
  );

  const ongoingOrdersList = useMemo(
    () =>
      collect(activeOrdersList)
        .whereNotIn('id', collect(futureOrdersList).pluck('id').toArray())
        .sortBy((item) => DateTime.fromISO(item.estimated_pickup_time).toUnixInteger())
        .toArray(),
    [activeOrdersList, futureOrdersList],
  );

  const pendingOrdersList = useMemo(
    () => collect(activeOrdersList).filter(OrderHelpers.isPending).sortBy('id').toArray(),
    [activeOrdersList],
  );

  const completeOrdersList = useMemo(
    () => collect(ordersList).whereNotIn('id', collect(activeOrdersList).pluck('id').toArray()).toArray(),
    [ordersList, activeOrdersList],
  );

  useEffect(() => {
    company?.id && loadOrdersList();
  }, [company?.id]);

  async function loadOrdersList() {
    setLoading(true);
    const { success, data } = await OrdersManager.get({
      paginated: false,
      sort: '-paid_at',
      filter: {
        pending: true,
        takeout: true,
      },
    });
    setLoading(false);

    if (!success) {
      return setFeedback({
        type: 'error',
        message: t('error_messages.failed_to_fetch_orders'),
      });
    }

    setOrdersList(data.data);
  }

  async function loadOrderDetails(id) {
    const { success, data } = await OrdersManager.show(id);

    if (!success) {
      throw new Error(t('error_messages.failed_to_fetch_orders'));
    }

    return data;
  }

  async function refreshSelectedOrder() {
    if (!selectedOrderRef.current?.id) {
      return;
    }

    loadOrderDetails(selectedOrderRef.current?.id)
      .then((orderData) => {
        replaceOrderData(orderData.data);
        setSelectedOrder(orderData.data);
      })
      .catch((error) =>
        setFeedback({
          type: 'error',
          message: error.message,
        }),
      );
  }

  async function markOrderHidden(order) {
    const { success } = await OrderFulfillmentsManager.update(order.fulfillment_id, {
      is_hidden_by_staff: true,
    });

    if (success) {
      return removeOrder(order.id);
    }

    return setFeedback({
      message: t('error_messages.something_went_wrong'),
      type: 'error',
    });
  }

  async function markOrderComplete(order) {
    if (order.fulfillment_status === OrderFulfillmentStatus.Rejected) {
      return markOrderHidden(order);
    }

    const response = await OrderFulfillmentsManager.update(order.fulfillment_id, {
      status: OrderHelpers.getCompletionFulfillmentStatus(order),
      is_hidden_by_staff: true,
    });

    if (response.success) {
      replaceOrderData(response.data.data);

      return response;
    }

    setFeedback({
      message: t('error_messages.something_went_wrong'),
      type: 'error',
    });

    return response;
  }

  const addOrder = (data) => setOrdersList((current) => collect(current).prepend(data).unique('id').toArray());
  const removeOrder = (id) => setOrdersList((current) => collect(current).where('id', '!=', id).toArray());
  const replaceOrderData = (data) => {
    if (selectedOrderRef.current?.id === data.id) {
      setSelectedOrder(data);
    }

    setOrdersList((current) =>
      collect(current)
        .map((item) => (item.id === data.id ? data : item))
        .toArray(),
    );
  };

  function toggleOrderDetailsModal(order = null) {
    setSelectedOrder(order);
    setShowOrderDetailsModal(!!order);
  }

  function toggleCompleteOrderModal(order = null) {
    setSelectedOrder(order);
    setShowCompleteOrderModal(!!order);
  }

  function toggleRejectOrderModal(order = null) {
    setSelectedOrder(order);
    setShowRejectOrderModal(!!order);
  }

  const context = {
    loading,
    ordersList: ordersListRef.current,
    activeOrdersList,
    ongoingOrdersList,
    futureOrdersList,
    scheduledOrdersList,
    pendingOrdersList,
    completeOrdersList,
    setOrdersList,
    addOrder,
    removeOrder,
    replaceOrderData,
    markOrderComplete,
    selectedOrder: selectedOrderRef.current,
    setSelectedOrder,
    loadOrderDetails,
    refreshSelectedOrder,
    toggleOrderDetailsModal,
    toggleCompleteOrderModal,
    toggleRejectOrderModal,
  };

  return (
    <OngoingOrdersContext.Provider value={context}>
      {children}
      {showOrderDetailsModal && Boolean(selectedOrderRef.current) && (
        <OrderDetailsModal
          show
          loading={loading}
          toggleModal={toggleOrderDetailsModal}
          order={selectedOrderRef.current}
        />
      )}
      {showCompleteOrderModal && Boolean(selectedOrderRef.current) && (
        <CompleteOrderModal order={selectedOrderRef.current} toggleModal={toggleCompleteOrderModal} />
      )}
      {showRejectOrderModal && Boolean(selectedOrderRef.current) && (
        <RejectOrderModal order={selectedOrderRef.current} />
      )}
    </OngoingOrdersContext.Provider>
  );
}
